// Heuristic gender detection for Web Speech API voices. The platform doesn't
// expose a gender field, so we look at keywords in the voice name and fall back
// to a curated list of well-known voice first-names per OS/browser.

const FEMALE_KEYWORDS = ["female", "woman", "girl"];
const MALE_KEYWORDS = ["male", " man ", "boy", "guy"];

// Known female voice names across Chrome / Edge / Safari / macOS / iOS / Android.
const FEMALE_NAMES = new Set([
  "aria", "jenny", "michelle", "ana", "allison", "ava", "samantha", "karen",
  "tessa", "moira", "veena", "fiona", "susan", "victoria", "zira", "hazel",
  "eva", "helena", "sabina", "catherine", "amelie", "amélie", "anna", "carmit",
  "damayanti", "ellen", "joana", "kanya", "kyoko", "laila", "lekha", "luciana",
  "mariska", "mei-jia", "melina", "milena", "monica", "mónica", "nora", "paulina",
  "sara", "satu", "ting-ting", "yelda", "yuna", "zosia", "zuzana", "siri female",
  "google us english female", "google uk english female", "natural female",
  "salli", "kimberly", "joanna", "matilda", "fenfen", "naayf",
]);

const MALE_NAMES = new Set([
  "guy", "ryan", "andrew", "brian", "kevin", "tom", "diego", "fred", "alex",
  "daniel", "oliver", "thomas", "maged", "rishi", "tarik", "kanya male", "albert",
  "bruce", "junior", "ralph", "natural male", "google us english male",
  "google uk english male", "siri male", "matthew", "joey", "justin", "russell",
])

const SCRIPTURE_SAMPLE = {
  en: "Thy word is a lamp unto my feet, and a light unto my path.",
  es: "Lámpara es a mis pies tu palabra, y lumbrera a mi camino.",
  fr: "Ta parole est une lampe à mes pieds, et une lumière sur mon sentier.",
  de: "Dein Wort ist meines Fußes Leuchte und ein Licht auf meinem Wege.",
  pt: "Lâmpada para os meus pés é a tua palavra, e luz para o meu caminho.",
  ru: "Слово Твое — светильник ноге моей и свет стезе моей.",
  hi: "तेरा वचन मेरे पांव के लिये दीपक, और मेरे मार्ग के लिये उजियाला है।",
  ar: "سراج لرجلي كلامك ونور لسبيلي.",
  "zh-CN": "你的话是我脚前的灯，是我路上的光。",
  sw: "Neno lako ni taa ya miguu yangu, na mwanga wa njia yangu.",
};

export function detectVoiceGender(voice) {
  if (!voice || !voice.name) return "other";
  const name = voice.name.toLowerCase();
  for (const kw of FEMALE_KEYWORDS) if (name.includes(kw)) return "female";
  for (const kw of MALE_KEYWORDS) if (name.includes(kw)) return "male";
  // First-word lookup (handles "Samantha", "Alex (Enhanced)" etc.)
  const firstToken = name.split(/[\s(]/)[0];
  if (FEMALE_NAMES.has(firstToken) || FEMALE_NAMES.has(name)) return "female";
  if (MALE_NAMES.has(firstToken) || MALE_NAMES.has(name)) return "male";
  return "other";
}

export function groupVoicesByGender(voices) {
  const female = [];
  const male = [];
  const other = [];
  for (const v of voices || []) {
    const g = detectVoiceGender(v);
    if (g === "female") female.push(v);
    else if (g === "male") male.push(v);
    else other.push(v);
  }
  return { female, male, other };
}

export function sampleSentenceFor(langCode) {
  return SCRIPTURE_SAMPLE[langCode] || SCRIPTURE_SAMPLE.en;
}

// Quick in-place preview (does not touch the global AudioContext queue).
export function speakSample(voice, rate = 0.95) {
  if (!voice || typeof window === "undefined" || !window.speechSynthesis) return;
  try { window.speechSynthesis.cancel(); } catch (_) { /* noop */ }
  const langCode = (voice.lang || "en").slice(0, 2).toLowerCase();
  const aliased = voice.lang && voice.lang.toLowerCase().startsWith("zh") ? "zh-CN" : langCode;
  const text = sampleSentenceFor(aliased);
  const u = new SpeechSynthesisUtterance(text);
  u.voice = voice;
  u.lang = voice.lang || "en-US";
  u.rate = rate;
  window.speechSynthesis.speak(u);
}
